// M13.2 — dual-axis line chart for the Depth section.
//
// Pageviews on the LEFT axis (a faint area + line in blurple — the
// dominant series, anchors the visual story). Active users on the RIGHT
// axis (a thinner line in purple-500). The two axes are independent
// linear scales so the gap between content consumption and audience
// reach is legible regardless of the absolute magnitudes.
//
// Mirrors the SourceMixChart React-SVG-with-d3-shape pattern so the two
// charts feel like siblings — same margin, same axis colours, same
// hover crosshair + tooltip surface. The shared PDF builder in
// src/shared/report/depth-svg.ts emits the equivalent static SVG.

import { useEffect, useMemo, useRef, useState } from "react";
import { bisector, max as d3Max } from "d3-array";
import { scaleLinear, scaleTime } from "d3-scale";
import { area as d3Area, line as d3Line } from "d3-shape";
import type { Granularity } from "./granularity";

export interface DepthChartPoint {
  /** ISO YYYY-MM-DD bucket. */
  date: string;
  pageviews: number;
  activeUsers: number;
  totalUsers: number;
}

export interface DepthChartProps {
  rows: DepthChartPoint[];
  granularity: Granularity;
  /** Used purely for the SVG aria-label — keeps the chart distinguishable
   *  to screen readers when multiple instances live on the same page. */
  ariaLabel?: string;
}

const DEFAULT_HEIGHT = 240;
// Wider left + right margins than SourceMixChart because we need to
// accommodate independent y-axis labels on both sides.
const MARGIN = { top: 28, right: 52, bottom: 28, left: 52 };
const AXIS_COLOR = "#6B7280"; // neutral-500
const GRID_COLOR = "#E5E7EB"; // neutral-200
const PAGEVIEW_COLOR = "#4F31FF"; // blurple
const PAGEVIEW_FILL = "rgba(79, 49, 255, 0.12)";
const USERS_COLOR = "#9583FF"; // purple-500

export function DepthChart({
  rows,
  granularity,
  ariaLabel = "Depth chart of pageviews and active users",
}: DepthChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(720);
  const [hoverX, setHoverX] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setWidth(Math.max(320, Math.floor(w)));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const height = DEFAULT_HEIGHT;
  const innerW = Math.max(10, width - MARGIN.left - MARGIN.right);
  const innerH = height - MARGIN.top - MARGIN.bottom;

  const dateObjs = useMemo(
    () => rows.map((r) => ({ ...r, dateObj: new Date(`${r.date}T00:00:00Z`) })),
    [rows],
  );

  const {
    xScale,
    yLeft,
    yRight,
    xTicks,
    yLeftTicks,
    yRightTicks,
    pageviewArea,
    pageviewLine,
    usersLine,
  } = useMemo(() => {
    const empty = {
      xScale: scaleTime().range([0, innerW]),
      yLeft: scaleLinear().range([innerH, 0]),
      yRight: scaleLinear().range([innerH, 0]),
      xTicks: [] as Date[],
      yLeftTicks: [] as number[],
      yRightTicks: [] as number[],
      pageviewArea: "",
      pageviewLine: "",
      usersLine: "",
    };
    if (dateObjs.length === 0) return empty;

    const [xMin, xMax] = [dateObjs[0].dateObj, dateObjs[dateObjs.length - 1].dateObj];
    const pageviewMaxRaw = d3Max(dateObjs, (d) => d.pageviews) ?? 0;
    const usersMaxRaw = d3Max(dateObjs, (d) => d.activeUsers) ?? 0;
    const pageviewMax = pageviewMaxRaw === 0 ? 1 : pageviewMaxRaw * 1.1;
    const usersMax = usersMaxRaw === 0 ? 1 : usersMaxRaw * 1.1;

    const xScale = scaleTime().domain([xMin, xMax]).range([0, innerW]);
    const yLeft = scaleLinear().domain([0, pageviewMax]).range([innerH, 0]);
    const yRight = scaleLinear().domain([0, usersMax]).range([innerH, 0]);

    const xTickCount = granularity === "day" ? 6 : granularity === "week" ? 5 : 4;
    const xTicks = xScale.ticks(xTickCount);
    const yLeftTicks = yLeft.ticks(4);
    const yRightTicks = yRight.ticks(4);

    const areaGen = d3Area<{ dateObj: Date; pageviews: number }>()
      .x((d) => xScale(d.dateObj))
      .y0(innerH)
      .y1((d) => yLeft(d.pageviews));
    const lineGenLeft = d3Line<{ dateObj: Date; pageviews: number }>()
      .x((d) => xScale(d.dateObj))
      .y((d) => yLeft(d.pageviews));
    const lineGenRight = d3Line<{ dateObj: Date; activeUsers: number }>()
      .x((d) => xScale(d.dateObj))
      .y((d) => yRight(d.activeUsers));

    return {
      xScale,
      yLeft,
      yRight,
      xTicks,
      yLeftTicks,
      yRightTicks,
      pageviewArea: areaGen(dateObjs) ?? "",
      pageviewLine: lineGenLeft(dateObjs) ?? "",
      usersLine: lineGenRight(dateObjs) ?? "",
    };
  }, [dateObjs, innerW, innerH, granularity]);

  const bisectDate = useMemo(() => bisector<{ dateObj: Date }, Date>((d) => d.dateObj).center, []);

  const onMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const xInside = event.clientX - rect.left - MARGIN.left;
    if (xInside < 0 || xInside > innerW) {
      setHoverX(null);
      return;
    }
    setHoverX(xInside);
  };

  const hoverInfo = useMemo(() => {
    if (hoverX === null || dateObjs.length === 0) return null;
    const dateAtX = xScale.invert(hoverX);
    const idx = bisectDate(dateObjs, dateAtX);
    const ref = dateObjs[Math.max(0, Math.min(dateObjs.length - 1, idx))];
    return {
      cx: xScale(ref.dateObj),
      date: ref.date,
      pageviews: ref.pageviews,
      activeUsers: ref.activeUsers,
      totalUsers: ref.totalUsers,
      pageviewY: yLeft(ref.pageviews),
      usersY: yRight(ref.activeUsers),
    };
  }, [hoverX, dateObjs, xScale, yLeft, yRight, bisectDate]);

  if (rows.length === 0) {
    return (
      <div
        ref={containerRef}
        className="flex h-56 items-center justify-center rounded-xl bg-neutral-50 text-sm text-neutral-500"
      >
        No depth data in the selected window.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel}
        className="ts-chart-reveal font-sans"
      >
        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          {/* Y-axis label LEFT (Pageviews) */}
          <text
            x={-MARGIN.left + 4}
            y={-12}
            fontSize={9}
            fontWeight={700}
            letterSpacing={1}
            fill={PAGEVIEW_COLOR}
          >
            PAGEVIEWS
          </text>
          {/* Y-axis label RIGHT (Active users) */}
          <text
            x={innerW + MARGIN.right - 4}
            y={-12}
            fontSize={9}
            fontWeight={700}
            letterSpacing={1}
            fill={USERS_COLOR}
            textAnchor="end"
          >
            ACTIVE USERS
          </text>

          {/* Y gridlines + LEFT-axis tick labels (pageviews) */}
          {yLeftTicks.map((v, i) => {
            const y = yLeft(v);
            return (
              <g key={`yl-${i}`}>
                <line x1={0} y1={y} x2={innerW} y2={y} stroke={GRID_COLOR} />
                <text x={-8} y={y + 3} fontSize={10} fill={PAGEVIEW_COLOR} textAnchor="end">
                  {formatYTick(v)}
                </text>
              </g>
            );
          })}
          {/* RIGHT-axis tick labels (active users) — no extra gridlines, just labels */}
          {yRightTicks.map((v, i) => {
            const y = yRight(v);
            return (
              <text
                key={`yr-${i}`}
                x={innerW + 8}
                y={y + 3}
                fontSize={10}
                fill={USERS_COLOR}
                textAnchor="start"
              >
                {formatYTick(v)}
              </text>
            );
          })}

          {/* X ticks */}
          {xTicks.map((d, i) => (
            <g key={`x-${i}`}>
              <line x1={xScale(d)} y1={innerH} x2={xScale(d)} y2={innerH + 4} stroke={AXIS_COLOR} />
              <text
                x={xScale(d)}
                y={innerH + 16}
                fontSize={10}
                fill={AXIS_COLOR}
                textAnchor="middle"
              >
                {formatXTick(d, granularity)}
              </text>
            </g>
          ))}

          {/* Pageview filled area + line (LEFT axis) */}
          {pageviewArea ? <path d={pageviewArea} fill={PAGEVIEW_FILL} /> : null}
          {pageviewLine ? (
            <path
              d={pageviewLine}
              fill="none"
              stroke={PAGEVIEW_COLOR}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : null}

          {/* Active users line (RIGHT axis) */}
          {usersLine ? (
            <path
              d={usersLine}
              fill="none"
              stroke={USERS_COLOR}
              strokeWidth={2}
              strokeDasharray="5 3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : null}

          {/* Hover crosshair + dots */}
          {hoverInfo ? (
            <>
              <line
                x1={hoverInfo.cx}
                y1={0}
                x2={hoverInfo.cx}
                y2={innerH}
                stroke={AXIS_COLOR}
                strokeDasharray="3 3"
                strokeWidth={1}
              />
              <circle
                cx={hoverInfo.cx}
                cy={hoverInfo.pageviewY}
                r={4}
                fill={PAGEVIEW_COLOR}
                stroke="#ffffff"
                strokeWidth={1.5}
              />
              <circle
                cx={hoverInfo.cx}
                cy={hoverInfo.usersY}
                r={4}
                fill={USERS_COLOR}
                stroke="#ffffff"
                strokeWidth={1.5}
              />
            </>
          ) : null}

          <rect
            x={0}
            y={0}
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={onMouseMove}
            onMouseLeave={() => setHoverX(null)}
          />
        </g>
      </svg>

      {/* Legend chips */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <span className="inline-flex items-center gap-1.5 text-xs text-neutral-700">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: PAGEVIEW_COLOR }}
          />
          <span className="font-semibold text-neutral-950">Pageviews</span>
          <span className="text-neutral-500">(left axis)</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-neutral-700">
          <span
            aria-hidden="true"
            className="inline-block h-[2px] w-4"
            style={{
              backgroundImage: `repeating-linear-gradient(to right, ${USERS_COLOR} 0 5px, transparent 5px 8px)`,
            }}
          />
          <span className="font-semibold text-neutral-950">Active users</span>
          <span className="text-neutral-500">(right axis)</span>
        </span>
      </div>

      {hoverInfo ? (
        <div
          className="pointer-events-none absolute -translate-x-1/2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-sm"
          style={{
            left: `${hoverInfo.cx + MARGIN.left}px`,
            top: `${MARGIN.top}px`,
            transform: `translate(-50%, -110%)`,
          }}
        >
          <p className="font-semibold text-neutral-950">{formatTooltipDate(hoverInfo.date)}</p>
          <ul className="mt-1 space-y-0.5">
            <li className="flex items-center gap-2 whitespace-nowrap">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: PAGEVIEW_COLOR }}
                aria-hidden="true"
              />
              <span className="text-neutral-600">Pageviews</span>
              <span className="ml-auto font-semibold tabular-nums text-neutral-950">
                {compactNumber(hoverInfo.pageviews)}
              </span>
            </li>
            <li className="flex items-center gap-2 whitespace-nowrap">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: USERS_COLOR }}
                aria-hidden="true"
              />
              <span className="text-neutral-600">Active users</span>
              <span className="ml-auto font-semibold tabular-nums text-neutral-950">
                {compactNumber(hoverInfo.activeUsers)}
              </span>
            </li>
            <li className="flex items-center gap-2 whitespace-nowrap text-neutral-500">
              <span className="inline-block h-2 w-2" aria-hidden="true" />
              <span>Total users</span>
              <span className="ml-auto tabular-nums">{compactNumber(hoverInfo.totalUsers)}</span>
            </li>
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function formatXTick(d: Date, granularity: Granularity): string {
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (granularity === "month") {
    return `${month[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  }
  return `${month[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function formatTooltipDate(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

function formatYTick(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 10_000) return `${(v / 1_000).toFixed(0)}K`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  if (Math.abs(v - Math.round(v)) < 0.05) return String(Math.round(v));
  return v.toFixed(1);
}

function compactNumber(n: number): string {
  if (!isFinite(n)) return "—";
  if (Math.abs(n) >= 10_000) {
    return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
  }
  return new Intl.NumberFormat("en").format(Math.round(n));
}
